import {
  COOKIE_NAMES,
  CSRF_HEADER,
  IDEMPOTENCY_HEADER,
  SUPPORT_ACCESS_HEADER,
  type ApiErrorCode,
  type ApiResponse,
  type ResponseMeta,
  isApiErrorResponse,
} from '@musleempay/shared';
import { clearSupportGrant, getSupportGrantId } from './support-access.js';

/**
 * HTTP client.
 *
 * The single place the browser talks to the API. It handles three things that
 * would otherwise be repeated in every hook:
 *
 *   1. Sending cookies and the CSRF header on state-changing requests.
 *   2. Unwrapping the `{ success, data, meta }` envelope.
 *   3. Turning an error envelope into a typed `ApiError` the UI can branch on.
 */

const API_BASE = import.meta.env['VITE_API_URL'] ?? '';

export class ApiError extends Error {
  constructor(
    readonly code: ApiErrorCode | string,
    message: string,
    readonly statusCode: number,
    readonly details?: readonly { field: string; message: string }[],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when signing in again would resolve it. */
  get isAuthError(): boolean {
    return this.statusCode === 401;
  }

  get isValidationError(): boolean {
    return this.code === 'VALIDATION_ERROR';
  }

  /** Field-level messages, keyed for React Hook Form's `setError`. */
  get fieldErrors(): Record<string, string> {
    const errors: Record<string, string> = {};
    for (const detail of this.details ?? []) {
      errors[detail.field] = detail.message;
    }
    return errors;
  }
}

/** Read the CSRF token the server set. It is intentionally not httpOnly. */
function readCsrfToken(): string | null {
  const match = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${COOKIE_NAMES.CSRF_TOKEN}=`));
  return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : null;
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly query?: Record<string, string | number | boolean | undefined | null>;
  readonly idempotencyKey?: string;
  readonly signal?: AbortSignal;
}

export interface ApiResult<T> {
  readonly data: T;
  readonly meta?: ResponseMeta;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  }

  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiResult<T>> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  // The double-submit half of CSRF protection.
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrfToken = readCsrfToken();
    if (csrfToken) headers[CSRF_HEADER] = csrfToken;
  }

  if (options.idempotencyKey) {
    headers[IDEMPOTENCY_HEADER] = options.idempotencyKey;
  }

  // While a platform operator is looking at a store, every call says so. The API
  // decides what that means (reads run as the store, changes are refused, platform
  // routes ignore it) — this just carries the id. For anyone else it is absent.
  const supportGrantId = getSupportGrantId();
  if (supportGrantId) headers[SUPPORT_ACCESS_HEADER] = supportGrantId;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      // Session cookies must travel with every request.
      credentials: 'include',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    // Network-level failure: no response at all.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(
      'SERVICE_UNAVAILABLE',
      'Could not reach the server. Check your connection and try again.',
      0,
    );
  }

  // 204 and other empty bodies.
  if (response.status === 204) {
    return { data: undefined as T };
  }

  let payload: ApiResponse<T> | null = null;
  try {
    payload = (await response.json()) as ApiResponse<T>;
  } catch {
    payload = null;
  }

  if (!response.ok || payload === null || isApiErrorResponse(payload)) {
    if (payload !== null && isApiErrorResponse(payload)) {
      // The grant lapsed (or was ended elsewhere). Every page under it is about
      // to start failing, so leave the view cleanly instead of showing a wall of
      // errors: drop the grant and go back to the platform area, reloading so no
      // store data stays cached in memory.
      if (payload.error.code === 'SUPPORT_ACCESS_EXPIRED') {
        clearSupportGrant();
        window.location.assign('/admin');
      }

      throw new ApiError(
        payload.error.code,
        payload.error.message,
        response.status,
        payload.error.details,
      );
    }
    throw new ApiError(
      'INTERNAL_ERROR',
      'Something went wrong. Please try again.',
      response.status,
    );
  }

  return { data: payload.data, ...(payload.meta ? { meta: payload.meta } : {}) };
}

/** Convenience wrappers — most callers only want `data`. */
export const api = {
  async get<T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal): Promise<T> {
    const result = await request<T>(path, {
      ...(query ? { query } : {}),
      ...(signal ? { signal } : {}),
    });
    return result.data;
  },

  /** For list endpoints, where the pagination meta matters. */
  async getPaged<T>(
    path: string,
    query?: RequestOptions['query'],
    signal?: AbortSignal,
  ): Promise<ApiResult<T>> {
    return request<T>(path, {
      ...(query ? { query } : {}),
      ...(signal ? { signal } : {}),
    });
  },

  async post<T>(path: string, body?: unknown, idempotencyKey?: string): Promise<T> {
    const result = await request<T>(path, {
      method: 'POST',
      ...(body !== undefined ? { body } : {}),
      ...(idempotencyKey ? { idempotencyKey } : {}),
    });
    return result.data;
  },

  async patch<T>(path: string, body: unknown): Promise<T> {
    const result = await request<T>(path, { method: 'PATCH', body });
    return result.data;
  },

  async put<T>(path: string, body: unknown): Promise<T> {
    const result = await request<T>(path, { method: 'PUT', body });
    return result.data;
  },

  async delete<T>(path: string): Promise<T> {
    const result = await request<T>(path, { method: 'DELETE' });
    return result.data;
  },
};

/** A key for an idempotent submission, stable for one attempt. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `key-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Human-readable message for any thrown value. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong. Please try again.';
}
